const PHOTO_CROP_PAN_PATCH = Symbol.for('eInvitation.photoAlbumCropPanBounds.v2')

const initializedOverlays = new WeakSet()
const cleanupByOverlay = new WeakMap()

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value))
}

function readControlValue(control, fallback = 50) {
  const value = Number(control?.value)
  return Number.isFinite(value) ? clamp(value, 0, 100) : fallback
}

function dispatchRangeInput(control, value) {
  const nextValue = String(Math.round(clamp(value, 0, 100)))
  if (control.value === nextValue) return
  control.value = nextValue
  control.dispatchEvent(new Event('input', { bubbles: true }))
}

function initializeCropOverlay(overlay) {
  if (!(overlay instanceof HTMLElement) || initializedOverlays.has(overlay)) return

  const viewport = overlay.querySelector('.epa-crop-viewport')
  const preview = viewport?.querySelector('img')
  const zoomControl = overlay.querySelector('[data-crop-control="zoom"]')
  const horizontalControl = overlay.querySelector('[data-crop-control="positionX"]')
  const verticalControl = overlay.querySelector('[data-crop-control="positionY"]')
  const shapeButtons = Array.from(overlay.querySelectorAll('[data-crop-shape]'))

  if (!(viewport instanceof HTMLElement) ||
      !(preview instanceof HTMLImageElement) ||
      !(zoomControl instanceof HTMLInputElement) ||
      !(horizontalControl instanceof HTMLInputElement) ||
      !(verticalControl instanceof HTMLInputElement)) {
    return
  }

  initializedOverlays.add(overlay)

  let animationFrame = 0
  let currentGeometry = null
  let dragState = null
  let cleanedUp = false

  function calculateGeometry() {
    const sourceWidth = preview.naturalWidth
    const sourceHeight = preview.naturalHeight
    const viewportWidth = viewport.clientWidth
    const viewportHeight = viewport.clientHeight

    if (!sourceWidth || !sourceHeight || !viewportWidth || !viewportHeight) return null

    const rawZoom = Number(zoomControl.value)
    const zoom = clamp(Number.isFinite(rawZoom) ? rawZoom : 1, 1, 3)
    const baseScale = Math.max(viewportWidth / sourceWidth, viewportHeight / sourceHeight)
    const renderedWidth = sourceWidth * baseScale * zoom
    const renderedHeight = sourceHeight * baseScale * zoom

    return {
      renderedWidth,
      renderedHeight,
      overflowX: Math.max(0, renderedWidth - viewportWidth),
      overflowY: Math.max(0, renderedHeight - viewportHeight),
    }
  }

  function updateControlState(control, enabled, axis) {
    control.disabled = !enabled
    control.closest('label')?.classList.toggle('is-pan-locked', !enabled)
    control.setAttribute(
      'aria-label',
      enabled
        ? `Move photo ${axis} within its available boundary`
        : `${axis} movement is available after zooming in`
    )

    if (!enabled && readControlValue(control) !== 50) dispatchRangeInput(control, 50)
  }

  function applyPreviewGeometry() {
    animationFrame = 0
    const geometry = calculateGeometry()
    if (!geometry) return

    currentGeometry = geometry

    const canPanX = geometry.overflowX > 0.5
    const canPanY = geometry.overflowY > 0.5
    updateControlState(horizontalControl, canPanX, 'left or right')
    updateControlState(verticalControl, canPanY, 'up or down')

    // Zero is a valid endpoint. Do not use `|| 50` here because it converts
    // the left/top boundary back to the center position.
    const positionX = canPanX ? readControlValue(horizontalControl) : 50
    const positionY = canPanY ? readControlValue(verticalControl) : 50

    preview.style.width = `${geometry.renderedWidth}px`
    preview.style.height = `${geometry.renderedHeight}px`
    preview.style.left = `${-(geometry.overflowX * positionX / 100)}px`
    preview.style.top = `${-(geometry.overflowY * positionY / 100)}px`
    preview.style.transform = 'none'
    preview.style.objectPosition = 'center'

    viewport.dataset.canPanX = canPanX ? 'true' : 'false'
    viewport.dataset.canPanY = canPanY ? 'true' : 'false'
  }

  function schedulePreviewUpdate() {
    if (animationFrame) cancelAnimationFrame(animationFrame)
    animationFrame = requestAnimationFrame(applyPreviewGeometry)
  }

  function resetPanToCenter() {
    dispatchRangeInput(horizontalControl, 50)
    dispatchRangeInput(verticalControl, 50)
    schedulePreviewUpdate()
  }

  function handlePointerDown(event) {
    if (event.button !== undefined && event.button !== 0) return

    applyPreviewGeometry()
    if (!currentGeometry || (currentGeometry.overflowX <= 0.5 && currentGeometry.overflowY <= 0.5)) return

    dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      positionX: readControlValue(horizontalControl),
      positionY: readControlValue(verticalControl),
      overflowX: currentGeometry.overflowX,
      overflowY: currentGeometry.overflowY,
    }

    viewport.classList.add('is-dragging')
    viewport.setPointerCapture?.(event.pointerId)
    event.preventDefault()
  }

  function handlePointerMove(event) {
    if (!dragState || dragState.pointerId !== event.pointerId) return

    if (dragState.overflowX > 0.5) {
      const nextX = dragState.positionX - ((event.clientX - dragState.startX) / dragState.overflowX * 100)
      dispatchRangeInput(horizontalControl, nextX)
    }

    if (dragState.overflowY > 0.5) {
      const nextY = dragState.positionY - ((event.clientY - dragState.startY) / dragState.overflowY * 100)
      dispatchRangeInput(verticalControl, nextY)
    }

    schedulePreviewUpdate()
    event.preventDefault()
  }

  function finishPointerDrag(event) {
    if (!dragState || (event.pointerId !== undefined && dragState.pointerId !== event.pointerId)) return
    viewport.releasePointerCapture?.(dragState.pointerId)
    dragState = null
    viewport.classList.remove('is-dragging')
  }

  const handleControlInput = () => schedulePreviewUpdate()
  const handlePreviewLoad = () => schedulePreviewUpdate()
  const handleShapeChange = () => requestAnimationFrame(resetPanToCenter)

  zoomControl.addEventListener('input', handleControlInput)
  horizontalControl.addEventListener('input', handleControlInput)
  verticalControl.addEventListener('input', handleControlInput)
  preview.addEventListener('load', handlePreviewLoad)
  shapeButtons.forEach((button) => button.addEventListener('click', handleShapeChange))

  viewport.addEventListener('pointerdown', handlePointerDown)
  viewport.addEventListener('pointermove', handlePointerMove)
  viewport.addEventListener('pointerup', finishPointerDrag)
  viewport.addEventListener('pointercancel', finishPointerDrag)
  viewport.addEventListener('lostpointercapture', finishPointerDrag)

  const resizeObserver = typeof ResizeObserver === 'function'
    ? new ResizeObserver(schedulePreviewUpdate)
    : null
  resizeObserver?.observe(viewport)

  function cleanup() {
    if (cleanedUp) return
    cleanedUp = true
    if (animationFrame) cancelAnimationFrame(animationFrame)
    resizeObserver?.disconnect()
    removalObserver.disconnect()

    zoomControl.removeEventListener('input', handleControlInput)
    horizontalControl.removeEventListener('input', handleControlInput)
    verticalControl.removeEventListener('input', handleControlInput)
    preview.removeEventListener('load', handlePreviewLoad)
    shapeButtons.forEach((button) => button.removeEventListener('click', handleShapeChange))

    viewport.removeEventListener('pointerdown', handlePointerDown)
    viewport.removeEventListener('pointermove', handlePointerMove)
    viewport.removeEventListener('pointerup', finishPointerDrag)
    viewport.removeEventListener('pointercancel', finishPointerDrag)
    viewport.removeEventListener('lostpointercapture', finishPointerDrag)
  }

  const removalObserver = new MutationObserver(() => {
    if (!overlay.isConnected) cleanup()
  })
  removalObserver.observe(document.documentElement, { childList: true, subtree: true })
  cleanupByOverlay.set(overlay, cleanup)

  schedulePreviewUpdate()
}

function scanForCropOverlays(root = document) {
  if (root instanceof Element && root.matches('.epa-crop-overlay')) initializeCropOverlay(root)
  root.querySelectorAll?.('.epa-crop-overlay').forEach(initializeCropOverlay)
}

export function installPhotoAlbumCropPanBounds() {
  if (typeof document === 'undefined' || globalThis[PHOTO_CROP_PAN_PATCH]) return

  const start = () => {
    scanForCropOverlays(document)

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue
          scanForCropOverlays(node)
        }

        for (const node of mutation.removedNodes) {
          if (!(node instanceof Element)) continue
          if (node.matches?.('.epa-crop-overlay')) cleanupByOverlay.get(node)?.()
          node.querySelectorAll?.('.epa-crop-overlay').forEach((overlay) => cleanupByOverlay.get(overlay)?.())
        }
      }
    })

    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true })
  } else {
    start()
  }

  globalThis[PHOTO_CROP_PAN_PATCH] = true
}
